import React, { useState, useContext, useMemo } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { Headline } from "../../components/Headline";
import FormField from "../../components/form-components/FormField";
import { FormSent } from "../FormSent/FormSent";

import FormService from "../../lib/service/formService";
import FormLayoutContext from "../../layouts/StandardFormLayout/context";
import t from "../../lib/translate";

import * as styles from "./component.module.less";
import { generateClassString } from "../../lib/helper/generateClassString";
import { resolveSessionValues } from "../resolveSessionValues";

import { slugify } from "../../lib/helper/getAnchorNavData";
import { isBrowser } from "../../lib/helper/variables";

import FocusError from "../FocusError";

export function SeminarRegistrationForm({
    formId,
    locale,
    data: { seminars = [] },
    locationState = {},
    isSending
}) {
    const [isSent, setIsSent] = useState(false);
    const { setShowSubmit } = useContext(FormLayoutContext);

    const getURLParams = () => {
        if (!isBrowser || !window.location.search)
            return { trainingIdParam: null, eventIdParam: null };
        const searchParams = new URLSearchParams(window.location.search);
        return {
            trainingIdParam: searchParams.get("training"),
            eventIdParam: searchParams.get("event")
        };
    };
    const { trainingIdParam, eventIdParam } = getURLParams();
    const availableSeminars = useMemo(() =>
        seminars
            .filter(
                (f) =>
                    f.upcomingAppointments.length > 0 &&
                    f.upcomingAppointments.some(({ seminarUrl }) => !seminarUrl)
            )
            .map((seminar) => {
                const upcomingAppointments = seminar.upcomingAppointments
                    .filter(({ seminarUrl }) => !seminarUrl)
                    .map(({ seminarStartDate, seminarEndDate, priceText, originalId }) => ({
                        eventDuration:
                            new Date(seminarStartDate).toLocaleDateString() +
                            " - " +
                            new Date(seminarEndDate).toLocaleDateString(),
                        priceText,
                        originalId: originalId
                    }));

                return {
                    ...seminar,
                    upcomingAppointments
                };
            })
    );

    const service = new FormService(
        process.env.GATSBY_FORM_MICROSERVICE_BASE_URL,
        locale
    );

    const initialTraining = availableSeminars.find(
        (s) => s.id === trainingIdParam
    );
    const initialEvent = initialTraining
        ? initialTraining.upcomingAppointments.find(
              (e) => e.originalId === eventIdParam
          )
        : null;
    const initialValues = {
        training: initialTraining
            ? initialTraining.id
            : availableSeminars[0]?.id,
        event: initialEvent
            ? initialEvent.eventDuration
            : availableSeminars[0]?.upcomingAppointments[0]?.eventDuration,
        email: "",
        salutation: "",
        title: "",
        firstname: "",
        lastname: "",
        company: "",
        department: "",
        phone: "",
        street: "",
        number: "",
        zip: "",
        city: "",
        country: "",
        taxIdNum: "",
        billingAddress: false,
        // billingAddressToggle: "",
        newsletter: "",
        gdpr: "",
        firstnameBilling: "",
        lastnameBilling: "",
        streetBilling: "",
        numberBilling: "",
        zipBilling: "",
        cityBilling: "",
        countryBilling: "",
        measuringTask: ""
    };

    const validationSchema = Yup.object({
        training: Yup.string().required(t("form.seminar.event.error")),
        event: Yup.string().required(t("form.seminar.appointment.error")),
        email: Yup.string().email().required(t("form.seminar.email.error")),
        salutation: Yup.string()
            .required(t("form.seminar.salutation.error"))
            .oneOf([
                t("form.seminar.salutation.value-01"),
                t("form.seminar.salutation.value-02"),
                t("form.seminar.salutation.value-03"),
                t("form.seminar.salutation.value-04")
            ]),
        title: Yup.string(),
        firstname: Yup.string().required(t("form.seminar.firstname.error")),
        lastname: Yup.string().required(t("form.seminar.surname.error")),
        company: Yup.string().required(t("form.seminar.company.error")),
        department: Yup.string(),
        phone: Yup.number().required(t("form.seminar.phone.error")),
        street: Yup.string(),
        number: Yup.string(),
        zip: Yup.string().required(t("form.seminar.zipcode.error")),
        city: Yup.string().required(t("form.seminar.city.error")),
        country: Yup.string(),
        taxIdNum: Yup.string(),
        billingAddress: Yup.boolean(),
        // billingAddressToggle: Yup.string(),
        newsletter: Yup.boolean(),
        gdpr: Yup.boolean()
            .oneOf([true], t("form.seminar.privacypolicy.error"))
            .required(t("form.seminar.privacypolicy.error")),
        firstnameBilling: Yup.string().when("billingAddress", {
            is: true,
            then: Yup.string().required(t("form.seminar.firstname.error"))
        }),
        lastnameBilling: Yup.string().when("billingAddress", {
            is: true,
            then: Yup.string().required(t("form.seminar.surname.error"))
        }),
        streetBilling: Yup.string(),
        numberBilling: Yup.string(),
        zipBilling: Yup.string().when("billingAddress", {
            is: true,
            then: Yup.string().required(t("form.seminar.zipcode.error"))
        }),
        cityBilling: Yup.string().when("billingAddress", {
            is: true,
            then: Yup.string().required(t("form.seminar.city.error"))
        }),
        countryBilling: Yup.string(),
        measuringTask: Yup.string()
    });

    const submit = (userData) => {
        const formData = {
            ...userData,
            language: locale,
            url: locationState?.prevUrl ?? "",
            gdpr: userData.gdpr ? "1" : "0",
            newsletter: userData.newsletter ? "1" : "0",
            billingAddress: userData.billingAddress ? "1" : "0",
            training: availableSeminars
                .find(({ id }) => id === userData.training)
                ?.name.trim()
                .replace(/[ä]/g, "ae")
                .replace(/[Ä]/g, "Ae")
                .replace(/[ö]/g, "oe")
                .replace(/[Ö]/g, "Oe")
                .replace(/[ü]/g, "ue")
                .replace(/[Ü]/g, "Ue")
                .replace(/[ß]/g, "ss")
                .replace(/[^\w\s-]/g, "")
                .replace(/[\s_-]+/g, "-")
                .replace(/^-+|-+$/g, "")
        };

        const { campaign, pnr, dnr } = resolveSessionValues();

        if (campaign) {
            formData.campaign = campaign.name;
        }

        if (pnr) {
            formData.pnr = pnr;
        }

        if (dnr) {
            formData.dnr = dnr;
        }
        isSending(true);
        service
            .submit("training-form", formData)
            .then((res) => {
                if (res === null) {
                    window?.dataLayer?.push({
                        event: "form_successfully_submitted"
                    });
                    setIsSent(true);
                    setShowSubmit(false);
                    isSending(false);
                }
            })
            .catch((error) => {
                console.error(error);
                isSending(false);
                throw error;
            });
    };

    return (
        <>
            {!isSent && (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={submit}
                >
                    {({ values, setFieldValue }) => {
                        const events =
                            availableSeminars?.find(
                                (f) => f.id === values.training
                            )?.upcomingAppointments ?? [];

                        const { priceText } =
                            events.find(
                                ({ eventDuration }) =>
                                    eventDuration === values.event
                            ) ?? {};

                        return (
                            <Form id={formId}>
                                <legend>
                                    <Headline semanticOfHeadline="H1">
                                        {t("form.seminar.headline")}
                                    </Headline>

                                    <div>{t("form.seminar.subheadline")}</div>
                                </legend>
                                <FormField
                                    type="select"
                                    name="training"
                                    label={t("form.seminar.event.label")}
                                    placeholder={t("form.seminar.event.label")}
                                    options={availableSeminars.map(
                                        ({ id, name }) => ({
                                            label: name,
                                            value: id
                                        })
                                    )}
                                    onChange={(event) => {
                                        setFieldValue(
                                            "training",
                                            event.target.value
                                        );
                                        setFieldValue(
                                            "event",
                                            availableSeminars.find(
                                                (f) =>
                                                    f.id === event.target.value
                                            )?.upcomingAppointments[0]
                                                .eventDuration
                                        );
                                    }}
                                    required
                                />
                                <FormField
                                    type="select"
                                    name="event"
                                    label={t("form.seminar.appointment.label")}
                                    // placeholder={t(
                                    //     "form.seminar.appointment.label"
                                    // )}
                                    options={
                                        events.map(
                                            ({ eventDuration }) => eventDuration
                                        ) || []
                                    }
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="email"
                                    label={t("form.seminar.email.label")}
                                    placeholder={t("form.seminar.email.label")}
                                    required
                                />
                                <FormField
                                    type="select"
                                    name="salutation"
                                    label={t("form.seminar.salutation.label")}
                                    placeholder={t(
                                        "form.seminar.salutation.label"
                                    )}
                                    options={[
                                        t("form.seminar.salutation.value-01"),
                                        t("form.seminar.salutation.value-02"),
                                        t("form.seminar.salutation.value-03"),
                                        ...t(
                                            "form.seminar.salutation.value-04"
                                        ).split(",")
                                    ]}
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="title"
                                    label={t("form.seminar.title.label")}
                                    placeholder={t("form.seminar.title.label")}
                                />
                                <FormField
                                    type="text"
                                    name="firstname"
                                    label={t("form.seminar.firstname.label")}
                                    placeholder={t(
                                        "form.seminar.firstname.label"
                                    )}
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="lastname"
                                    label={t("form.seminar.surname.label")}
                                    placeholder={t(
                                        "form.seminar.surname.label"
                                    )}
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="company"
                                    label={t("form.seminar.company.label")}
                                    placeholder={t(
                                        "form.seminar.company.label"
                                    )}
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="department"
                                    label={t("form.seminar.department.label")}
                                    placeholder={t(
                                        "form.seminar.department.label"
                                    )}
                                />
                                <FormField
                                    type="text"
                                    name="phone"
                                    label={t("form.seminar.phone.label")}
                                    placeholder={t("form.seminar.phone.label")}
                                    required
                                />

                                {/* Address */}
                                <FormField
                                    type="text"
                                    name="street"
                                    label={t("form.seminar.street.label")}
                                    placeholder={t("form.seminar.street.label")}
                                />
                                <FormField
                                    type="text"
                                    name="number"
                                    label={t("form.seminar.housenumber.label")}
                                    placeholder={t(
                                        "form.seminar.housenumber.label"
                                    )}
                                />
                                <FormField
                                    type="text"
                                    name="zip"
                                    label={t("form.seminar.zipcode.label")}
                                    placeholder={t(
                                        "form.seminar.zipcode.label"
                                    )}
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="city"
                                    label={t("form.seminar.city.label")}
                                    placeholder={t("form.seminar.city.label")}
                                    required
                                />
                                <FormField
                                    type="text"
                                    name="country"
                                    label={t("form.seminar.country.label")}
                                    placeholder={t(
                                        "form.seminar.country.label"
                                    )}
                                />

                                <FormField
                                    type="text"
                                    name="taxIdNum"
                                    label={t("form.seminar.tax-idnr.label")}
                                    placeholder={t(
                                        "form.seminar.tax-idnr.label"
                                    )}
                                />
                                <FormField
                                    type="checkbox"
                                    name="billingAddress"
                                    label={t(
                                        "form.seminar.billingaddress.checkbox"
                                    )}
                                    placeholder={t(
                                        "form.seminar.billingaddress.checkbox"
                                    )}
                                />
                                {/* Billing Address */}

                                <div
                                    className={generateClassString(
                                        styles.alternativeBillingAddress,
                                        [!values.billingAddress, styles.hidden]
                                    )}
                                >
                                    <FormField
                                        type="text"
                                        name="firstnameBilling"
                                        label={t(
                                            "form.seminar.firstname.label"
                                        )}
                                        placeholder={t(
                                            "form.seminar.firstname.label"
                                        )}
                                        required
                                    />
                                    <FormField
                                        type="text"
                                        name="lastnameBilling"
                                        label={t("form.seminar.surname.label")}
                                        placeholder={t(
                                            "form.seminar.surname.label"
                                        )}
                                        required
                                    />
                                    <FormField
                                        type="text"
                                        name="streetBilling"
                                        label={t("form.seminar.street.label")}
                                        placeholder={t(
                                            "form.seminar.street.label"
                                        )}
                                    />
                                    <FormField
                                        type="text"
                                        name="numberBilling"
                                        label={t(
                                            "form.seminar.housenumber.label"
                                        )}
                                        placeholder={t(
                                            "form.seminar.housenumber.label"
                                        )}
                                    />
                                    <FormField
                                        type="text"
                                        name="zipBilling"
                                        label={t("form.seminar.zipcode.label")}
                                        placeholder={t(
                                            "form.seminar.zipcode.label"
                                        )}
                                        required
                                    />
                                    <FormField
                                        type="text"
                                        name="cityBilling"
                                        label={t("form.seminar.city.label")}
                                        placeholder={t(
                                            "form.seminar.city.label"
                                        )}
                                        required
                                    />
                                    <FormField
                                        type="text"
                                        name="countryBilling"
                                        label={t("form.seminar.country.label")}
                                        placeholder={t(
                                            "form.seminar.country.label"
                                        )}
                                    />
                                </div>

                                <FormField
                                    type="textarea"
                                    name="application"
                                    label={t(
                                        "form.seminar.measuringtask.label"
                                    )}
                                    placeholder={t(
                                        "form.seminar.measuringtask.label"
                                    )}
                                />

                                <FormField
                                    type="checkbox"
                                    name="newsletter"
                                    label={t(
                                        "form.seminar.newsletter.checkbox"
                                    )}
                                    placeholder={t(
                                        "form.seminar.newsletter.checkbox"
                                    )}
                                />
                                <FormField
                                    type="checkbox"
                                    name="gdpr"
                                    label={t(
                                        "form.seminar.privacypolicy.checkbox"
                                    )}
                                    placeholder={t(
                                        "form.seminar.privacypolicy.checkbox"
                                    )}
                                    required
                                />

                                {t("form.seminar.subtitle")}

                                {/* <div
                                    className={generateClassString(
                                        styles.priceInformation,
                                        [!priceText, styles.hidden]
                                    )}
                                >
                                    <Headline
                                        headlineClass={
                                            styles.priceInformationHeadline
                                        }
                                        semanticOfHeadline="H5"
                                    >
                                        {t(
                                            "form.seminar.priceinformation.headline"
                                        )}
                                    </Headline>
                                    <div>{priceText}</div>
                                </div> */}
                                <FocusError />
                            </Form>
                        );
                    }}
                </Formik>
            )}
            {isSent && <FormSent />}
        </>
    );
}
